// Eszköz-katalógus kezelő űrlapja (Ilcsa #113) — a KATEGÓRIA és a TÍPUS egy űrlap két alakja (a `kind` váltja).
// A validálás forrás-igazsága a @atlas/contracts (AssetCategoryWriteBodySchema / AssetTypeWriteBodySchema): a
// resolver a wire-alakon fut, a hibát a lapos form-mezőkre köti (a beágyazott `name.hu` → `nameHu`).
import {
  AssetCategoryWriteBodySchema,
  AssetTypeWriteBodySchema,
  type AssetCategoryRowSchema,
  type AssetTypeRowSchema,
} from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

export type CatalogKind = 'category' | 'type';

type CategoryRow = z.infer<typeof AssetCategoryRowSchema>;
type TypeRow = z.infer<typeof AssetTypeRowSchema>;
export type CategoryWriteBody = z.infer<typeof AssetCategoryWriteBodySchema>;
export type TypeWriteBody = z.infer<typeof AssetTypeWriteBodySchema>;

export interface CatalogFormValues {
  kind: CatalogKind;
  nameHu: string;
  nameEn: string;
  /** Csak kategóriánál: a leltári szám előtagja (nagybetű/szám, 2–8). */
  numberPrefix: string;
  /** Csak kategóriánál: szoftver-jellegű (licenckulcs + lejárat). */
  isSoftware: boolean;
  /** Csak típusnál: a kategória azonosítója. */
  categoryId: string;
}

export const EMPTY_CATALOG_FORM: CatalogFormValues = {
  kind: 'category',
  nameHu: '',
  nameEn: '',
  numberPrefix: '',
  isSoftware: false,
  categoryId: '',
};

export function catalogFormFromCategory(row: CategoryRow): CatalogFormValues {
  return {
    kind: 'category',
    nameHu: row.name.hu,
    nameEn: row.name.en ?? '',
    numberPrefix: row.numberPrefix,
    isSoftware: row.isSoftware,
    categoryId: '',
  };
}

export function catalogFormFromType(row: TypeRow): CatalogFormValues {
  return {
    kind: 'type',
    nameHu: row.name.hu,
    nameEn: row.name.en ?? '',
    numberPrefix: '',
    isSoftware: false,
    categoryId: row.categoryId,
  };
}

function nameOf(v: CatalogFormValues): { hu: string; en: string | null } {
  const en = v.nameEn.trim();
  return { hu: v.nameHu.trim(), en: en === '' ? null : en };
}

/** Az előtag a mentéskor nagybetűsre normalizálódik (a contract regexe csak A–Z / 0–9-et enged). */
export function toCategoryBody(v: CatalogFormValues): CategoryWriteBody {
  return {
    name: nameOf(v),
    numberPrefix: v.numberPrefix.trim().toUpperCase(),
    isSoftware: v.isSoftware,
  };
}

export function toTypeBody(v: CatalogFormValues): TypeWriteBody {
  return { name: nameOf(v), categoryId: v.categoryId };
}

/** Lokalizált zod-hibatérkép a katalógus-űrlaphoz. */
function buildCatalogErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'name') return { message: t('validation:assets.catalog.name') };
    if (field === 'numberPrefix') return { message: t('validation:assets.catalog.prefix') };
    if (field === 'categoryId') return { message: t('validation:assets.catalog.category') };
    return { message: ctx.defaultError };
  };
}

export function buildCatalogResolver(t: TFunction): Resolver<CatalogFormValues> {
  const errorMap = buildCatalogErrorMap(t);
  const categoryBase = zodResolver(AssetCategoryWriteBodySchema, {
    errorMap,
  }) as unknown as Resolver<CatalogFormValues>;
  const typeBase = zodResolver(AssetTypeWriteBodySchema, {
    errorMap,
  }) as unknown as Resolver<CatalogFormValues>;
  return async (values, context, options) => {
    const isCategory = values.kind === 'category';
    const wire = (isCategory ? toCategoryBody(values) : toTypeBody(values)) as unknown;
    const result = await (isCategory ? categoryBase : typeBase)(
      wire as CatalogFormValues,
      context,
      options,
    );
    // A contract a nevet BEÁGYAZOTT objektumként validálja (`name.hu`) — a form KÉT lapos mezőt ismer, ezért a
    // hibát a magyar név mezőjére kötjük (az a kötelező).
    const errors: Record<string, unknown> = { ...result.errors };
    if ('name' in errors) {
      errors.nameHu ??= errors.name;
      delete errors.name;
    }
    if (Object.keys(errors).length > 0) {
      return { values: {}, errors: errors as typeof result.errors };
    }
    return { values, errors: {} };
  };
}
